import { mapPreviewUrl } from "@dailywars/core/engine/maps/mapId";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "#/lib/utils";

type MapPreviewProps = {
  mapId: string;
  maxSize: number;
  className?: string;
  children?: ReactNode;
};

function MapPreviewImg({
  mapId,
  className,
  style,
}: {
  mapId: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <img
      src={mapPreviewUrl(mapId)}
      alt=""
      className={cn("block h-auto w-auto [image-rendering:pixelated]", className)}
      style={style}
    />
  );
}

function FillWidthMapPreview({ mapId, maxSize, className, children }: MapPreviewProps) {
  const previewMaxStyle = {
    maxWidth: maxSize,
    "--preview-max": `${maxSize}px`,
  } as CSSProperties;

  return (
    <div
      className="@container/preview mx-auto aspect-square w-full min-h-0 min-w-0 overflow-hidden"
      style={previewMaxStyle}
    >
      <div className="grid size-full place-items-center">
        <div className={cn("relative w-fit max-h-full max-w-full", className)}>
          <MapPreviewImg
            mapId={mapId}
            className="max-h-[min(100cqmin,var(--preview-max))] max-w-[min(100cqmin,var(--preview-max))]"
          />
          {children}
        </div>
      </div>
    </div>
  );
}

function SquareSlotMapPreview({ mapId, maxSize, className, children }: MapPreviewProps) {
  const maxBounds = { maxWidth: maxSize, maxHeight: maxSize };

  return (
    <div
      className="flex shrink-0 items-center justify-center"
      style={{ width: maxSize, height: maxSize }}
    >
      <div className={cn("relative w-fit overflow-hidden", className)}>
        <MapPreviewImg mapId={mapId} style={maxBounds} />
        {children}
      </div>
    </div>
  );
}

function NaturalMapPreview({ mapId, maxSize, className, children }: MapPreviewProps) {
  const maxBounds = { maxWidth: maxSize, maxHeight: maxSize };

  return (
    <div className={cn("relative mx-auto w-fit max-w-full shrink-0 overflow-hidden", className)}>
      <MapPreviewImg mapId={mapId} className="max-w-full" style={maxBounds} />
      {children}
    </div>
  );
}

export function MapPreviewImage({
  mapId,
  maxSize,
  fillWidth = false,
  squareSlot = false,
  className,
  children,
}: MapPreviewProps & {
  fillWidth?: boolean;
  squareSlot?: boolean;
}) {
  const props = { mapId, maxSize, className, children };

  if (fillWidth) {
    return <FillWidthMapPreview {...props} />;
  }

  if (squareSlot) {
    return <SquareSlotMapPreview {...props} />;
  }

  return <NaturalMapPreview {...props} />;
}
